/**
 * Global app state — plain Vue reactive object, no Pinia (per D-010).
 *
 * Persisted slices in localStorage:
 *   - theme ("light" | "dark")
 *   - sidebarCollapsed (boolean)
 *   - uiLanguage ("en" | "da" — only "en" is wired up in fase A4)
 *   - displayCurrency ("DKK" | "EUR" | "USD")
 *
 * Other slices (filters, view-specific state) will be added per page as
 * needed. Filters auto-refresh data via watchers in respective views.
 */

import { reactive, watch } from 'vue';

export type Theme = 'light' | 'dark';
export type Language = 'en' | 'da';
export type Currency = 'DKK' | 'EUR' | 'USD';

interface AppState {
  theme: Theme;
  sidebarCollapsed: boolean;
  uiLanguage: Language;
  displayCurrency: Currency;
  /** Brand-udtrykket ([D-471]); standard for alle profiler ([D-522]), slås
   *  kun fra, når brugeren har valgt Klassisk på Min profil. */
  brandUi: boolean;
}

function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  const value = localStorage.getItem(key);
  return allowed.includes(value as T) ? (value as T) : fallback;
}

function loadInitialState(): AppState {
  const prefersDark =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-color-scheme: dark)').matches;

  return {
    theme: readStored('theme', ['light', 'dark'] as const, prefersDark ? 'dark' : 'light'),
    sidebarCollapsed: localStorage.getItem('sidebarCollapsed') === 'true',
    uiLanguage: readStored('uiLanguage', ['da', 'en'] as const, 'da'),
    displayCurrency: readStored('displayCurrency', ['DKK', 'EUR', 'USD'] as const, 'DKK'),
    brandUi: localStorage.getItem('brandUi') !== 'off',
  };
}

export const state = reactive<AppState>(loadInitialState());

/** Toggles the `dark` class on <html> so Tailwind's dark-mode rules apply. */
function applyTheme(theme: Theme): void {
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

// Apply on first load (also handled by FOUC-init script in index.html, but
// this keeps Vue state in sync if the user changes theme mid-session).
applyTheme(state.theme);

// Persist + reflect changes back to DOM.
watch(
  () => state.theme,
  (theme) => {
    localStorage.setItem('theme', theme);
    applyTheme(theme);
  },
);

watch(
  () => state.sidebarCollapsed,
  (collapsed) => {
    localStorage.setItem('sidebarCollapsed', String(collapsed));
  },
);

watch(
  () => state.uiLanguage,
  (lang) => {
    localStorage.setItem('uiLanguage', lang);
  },
);

watch(
  () => state.brandUi,
  (on) => {
    localStorage.setItem('brandUi', on ? 'on' : 'off');
  },
);

watch(
  () => state.displayCurrency,
  (currency) => {
    localStorage.setItem('displayCurrency', currency);
  },
);
